/**
 * #1018 — the Polymarket US counterpart of the Polymarket Account card.
 *
 * Read-only: lists the US accounts this login can see and whether each can
 * trade right now. Accounts and keys are set up by a manager in organization
 * settings, so there is nothing to enter here. The relay's account list is
 * already scoped to this login's own grants — a personal account is visible
 * only to its owner — so another trader's account can never appear.
 *
 * Keeps the same card while checking, empty or unavailable (#1264), so the
 * account section does not appear after the rest of Settings has settled. A
 * list that cannot be read is said plainly, with a Retry (#1113). Each status read is
 * bounded, so a hung read ends at "Status unknown" rather than "Checking…".
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import {
  listVisibleUsAccounts,
  type UsAccountContext,
  type UsVisibleAccount,
} from '@/lib/polymarketUs/accountClient';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { usAccountLabel, usReason, usReasonStatus } from '@/views/PolymarketUs/usVenueLabel';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { HelpPopover } from '@/components/molecules/HelpPopover';

type Tone = 'ready' | 'limited' | 'down' | 'off';
/** `detail` is the ticket's sentence for the same state, shown on hover. */
export interface UsAccountStatus { label: string; tone: Tone; detail?: string }

/** #1114 T8: quick references for the three access states; a status's own `detail` wins where set. */
const STATUS_TITLE: Readonly<Record<string, string>> = {
  Ready: 'Ready: you can place and cancel.',
  'Cancel only': 'Cancel only: you can cancel your orders but not place.',
  'View only': 'View only: read-only for this login.',
};

/**
 * What a trader needs to know about one account. `context` is null when the
 * account's context read failed; the row stays, with an honest "unknown" —
 * including when access was revoked between the list and this read, which
 * reload clears (dropping the row would renumber it away from the ticket).
 * The context read is newer than the list row, so its enabled flag wins.
 */
export function usAccountStatus(account: UsVisibleAccount, context: UsAccountContext | null): UsAccountStatus {
  if (!(context?.accountEnabled ?? account.accountEnabled)) return { label: 'Disabled', tone: 'off' };
  if (!context) return { label: 'Status unknown', tone: 'off' };
  const r = context.readiness;
  if (r.state === 'unavailable') {
    return {
      label: usReasonStatus(r.reason),
      tone: r.reason === 'US_EXECUTION_NOT_ACTIVATED' ? 'off' : 'down',
      detail: usReason(r.reason),
    };
  }
  if (r.canSubmit) return { label: 'Ready', tone: 'ready' };
  if (r.canCancelOwn || r.canCancelDesk) return { label: 'Cancel only', tone: 'limited' };
  return { label: 'View only', tone: 'off' };
}

const DOT: Record<Tone, string> = {
  ready: 'bg-green-500',
  limited: 'bg-yellow-500',
  down: 'bg-red-500',
  off: 'bg-muted-foreground/40',
};

interface Row { account: UsVisibleAccount; label: string; status: UsAccountStatus | null }

/** Own-view accounts first, numbered exactly as the Game Detail ticket numbers
 * them; desk-view-only accounts (never on the ticket) follow, unnumbered. */
function labelRows(accounts: UsVisibleAccount[]): Row[] {
  const own = accounts.filter((a) => a.canViewOwn);
  const deskOnly = accounts.filter((a) => !a.canViewOwn);
  return [
    ...own.map((account, i) => ({ account, label: usAccountLabel(account.ownership, i, own.length), status: null })),
    ...deskOnly.map((account) => ({ account, label: 'Desk view only', status: null })),
  ];
}

const subscribe = (notify: () => void) => useAuthStore.subscribe(notify);

/** #1113: how long one account's status read may take before its row says "Status unknown". */
export const US_ACCOUNT_ROW_READ_TIMEOUT_MS = 10_000;

/** `read`, or a rejection after `ms` (the read itself is left to the client, which drops a superseded answer). */
function bounded<T>(read: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('US account read timed out')), ms); });
  return Promise.race([read, timeout]).finally(() => clearTimeout(timer));
}

type Loaded = { epoch: number; rows: Row[] } | { epoch: number; failed: true };

export function PolymarketUsAccountsCard() {
  const epoch = useSyncExternalStore(subscribe, getAuthSessionEpoch);
  // Like useUsAccountPresence: a read before login completes would fail and
  // leave the card hidden for the whole epoch.
  const authenticated = useAuthStore((s) => s.status === 'authenticated');
  const role = useAuthStore((s) => s.user?.role);
  const canManage = authenticated && (role === 'admin' || role === 'manager');
  // Keyed by login epoch: a replacement login never sees the previous rows.
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  // #1113: bumped by Retry after a failed list read; the read effect re-runs on it.
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const current = loaded?.epoch === epoch ? loaded : null;
  const rows = current && 'rows' in current ? current.rows : null;
  const failed = !!current && 'failed' in current;

  useEffect(() => {
    if (!authenticated) return;
    const client = createUsBrowserAccountClient();
    let cancelled = false;
    (async () => {
      const listed = labelRows(await listVisibleUsAccounts(client));
      if (cancelled) return;
      setRetrying(false);
      setLoaded({ epoch, rows: listed });
      // One at a time: each read on this client supersedes the previous one.
      for (let i = 0; i < listed.length; i++) {
        const { account } = listed[i];
        let context: UsAccountContext | null = null;
        try {
          context = await bounded(client.selectAccount(account.accountId, account.canViewOwn ? 'own' : 'desk'),
            US_ACCOUNT_ROW_READ_TIMEOUT_MS);
        } catch {
          // Row keeps "Status unknown" (a failed or hung read); saved access is still worth showing.
        }
        if (cancelled) return;
        const status = usAccountStatus(account, context);
        setLoaded((prev) => (prev && 'rows' in prev
          ? { epoch, rows: prev.rows.map((row, j) => (j === i ? { ...row, status } : row)) } : prev));
      }
    })().catch(() => {
      // #1113: an unreadable list is its own state, never an empty one.
      if (!cancelled) {
        setRetrying(false);
        setLoaded({ epoch, failed: true });
      }
    });
    return () => {
      cancelled = true;
      client.invalidate();
    };
  }, [epoch, authenticated, attempt]);

  return (
    <div data-testid="polymarket-us-accounts-card" className="bg-card border-border rounded-lg border p-5">
      <h2 className="mb-4 text-base font-medium">Polymarket US</h2>
      <div className="min-h-10">
        {failed ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span data-testid="polymarket-us-accounts-failed">Couldn't read your Polymarket US accounts</span>
            <button
              type="button"
              aria-disabled={retrying || undefined}
              onClick={() => {
                if (retrying) return;
                setRetrying(true);
                setAttempt((n) => n + 1);
              }}
              className="border-border hover:bg-muted shrink-0 rounded border px-2 py-0.5 text-xs font-medium aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        ) : !rows ? (
          <p className="text-muted-foreground text-sm" role="status" aria-busy="true">Checking accounts…</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="polymarket-us-accounts-empty">
            No US account is available to this login.
          </p>
        ) : (
          <ul className="divide-border divide-y">
            {rows.map(({ account, label, status }) => (
              <li
                key={account.accountId}
                data-testid="polymarket-us-account-row"
                className="flex items-center justify-between gap-3 py-2 text-sm first:pt-0 last:pb-0"
              >
                <span className="min-w-0 truncate" title={account.accountId}>{label}</span>
                <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                  {status ? (
                    <>
                      <span className={`h-2 w-2 rounded-full ${DOT[status.tone]}`} />
                      {status.detail ?? STATUS_TITLE[status.label] ? (
                        <HelpPopover
                          label={`${label}: ${status.label} details`}
                          title={`${label}: ${status.label}`}
                          trigger={<span data-testid="polymarket-us-account-status" title={status.detail ?? STATUS_TITLE[status.label]}>{status.label}</span>}
                          triggerClassName="underline decoration-dotted underline-offset-4 hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                          srDescription={status.detail ?? STATUS_TITLE[status.label]}
                        >
                          {status.detail ?? STATUS_TITLE[status.label]}
                        </HelpPopover>
                      ) : <span data-testid="polymarket-us-account-status">{status.label}</span>}
                    </>
                  ) : (
                    'Checking…'
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-muted-foreground mt-4 text-xs">
        {canManage ? (
          <>Manage accounts and keys in <Link to="/organization/org-settings" className="text-primary underline underline-offset-2">Organization Settings</Link>.</>
        ) : 'Your manager controls US account access and keys.'}
      </p>
    </div>
  );
}
